// src/render/audio/manager.ts — Phase 6 Howler audio manager singleton.
//
// CONTEXT D-17: subscribed ONE-WAY to renderBus (events in → sounds out;
// nothing emitted back). AUDIO-01 invariant: this module's source has zero
// renderBus emit calls; manager.test.ts greps for the emit pattern and fails
// the build if a back-edge is ever introduced. (06-07's planned debug
// audio-debug telemetry is runtime-gated by isDebugEnabled(), with its own test.)
//
// Lifecycle:
//   - init() is async + idempotent. Loads /assets/audio/audio-manifest.json
//     via fetch (WARNING-3: NOT a build-time JSON import — Vite would force
//     it into the bundle and defeat the precache strategy). Calls each
//     submodule's initX(codec, manifest); subscribes to 11 renderBus events.
//   - destroy() unsubscribes everything + tears down submodule state +
//     drains the BLOCKER-3 liveSources Set + Howler.unload(). Idempotent.
//   - trackHowl(howl) is the BLOCKER-3 source-of-truth wiring: every
//     downstream Howl constructed in 06-05/06-06 MUST be passed through
//     this method so getLiveSources() returns a real count of active
//     playback IDs (used by 06-07 DEV audio-debug emit and by 06-08
//     phase6-integration.test.ts AUDIO-07 ≤8 ceiling proof).
//
// Tier-isolation: imports allowed from src/sim/content/cache (shared-tier
// contract). NOT permitted: src/sim/characters/profiles or any deeper sim
// path. The cache layer is the ONLY sim-shaped data audio reads.
//
// SOURCE: 06-CONTEXT.md D-17, D-20; 06-RESEARCH.md Example 1; 06-PATTERNS.md
//         §src/render/audio/manager.ts.

import { Howl, Howler } from 'howler';

import { isDebugEnabled } from '../../debug-flag';
import { renderBus } from '../bus';
import {
  decayDialogueFatigue,
  getQuote,
  hasContentBundle,
} from '../../sim/content/cache';

import { selectCodec, srcListFor, type AudioCodec, type AudioManifest } from './codec';
import { unlock as unlockHelper } from './unlock';
import {
  P6_HEARTBEAT_BASE_BPM,
  P7_MASTER_DEFAULT,
  P7_BUS_MUSIC_DEFAULT,
  P7_BUS_SFX_DEFAULT,
} from './constants';
import type { StingerName } from './stinger-registry';

import { initAmbient, destroyAmbient } from './ambient';
import {
  initMusic,
  destroyMusic,
  onRoundStarted,
  onRoundEnded,
} from './music';
import {
  initStingers,
  playStinger as _playStinger,
  stopMotionHum,
  destroyStingers,
} from './stingers';
import {
  initWaffle,
  playWaffleLine as _playWaffleLine,
  cancelWaffleLine as _cancelWaffle,
  destroyWaffle,
} from './waffle';
import {
  initFootsteps,
  onEntityMoved as _onEntityMoved,
  destroyFootsteps,
} from './footsteps';

// ===========================================================================
// Module-level state (singleton)
// ===========================================================================

let initialized = false;
let codec: AudioCodec = 'opus';

/**
 * BLOCKER-3 — real Set<number> of active Howler playback IDs. Submodules
 * register Howls via audioManager.trackHowl(howl); this manager wires
 * 'play' (id added), 'end' / 'stop' (id removed) listeners on every Howl.
 *
 * getLiveSources() returns liveSources.size — used by:
 *   - 06-07 DEV audio-debug emit (live concurrent count for DevOverlay)
 *   - 06-08 phase6-integration.test.ts (AUDIO-07 ≤8 concurrent ceiling proof)
 *
 * Cleared by destroy() so a fresh init() starts from zero.
 */
const liveSources: Set<number> = new Set();

/**
 * AudioBus = 'music' | 'sfx'. Howler 2.2.x has NO native bus (07-RESEARCH
 * §"The Audio Bus Split"), so the manager keeps its own registry of every
 * tracked Howl tagged with a bus + its design (authored) volume.
 *
 * `setVolumes({master, music, sfx})` (UI-06) sets the Howler global master and
 * recomputes each registered Howl's group volume to `designVolume * busGain[bus]`.
 * Music bus = calm + wind-down score loops ONLY; SFX bus = ambient night-bed,
 * stingers, footsteps, waffle, heartbeat — so the Music slider silences only the
 * round score while the world stays alive (07-PATTERNS §bus split, A2).
 */
type AudioBus = 'music' | 'sfx';

const registry: Array<{ howl: Howl; bus: AudioBus; designVolume: number }> = [];

const busGain: { music: number; sfx: number } = {
  music: P7_BUS_MUSIC_DEFAULT,
  sfx: P7_BUS_SFX_DEFAULT,
};

/**
 * RenderBus subscription cleanup callbacks. Each `renderBus.on(...)` returns
 * an off-fn; we collect them here and invoke every one in destroy().
 */
const subscriptions: Array<() => void> = [];

/**
 * Clamp a seeded gain to [0,1] (T-07-01 — hunter:settings is client-controlled,
 * same-origin localStorage; malformed values must not drive Howler out of range).
 */
function clampGain(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Plan 06-07 — heartbeat Howl + cached playback id.
 *
 * The heartbeat is a single recorded sample at P6_HEARTBEAT_BASE_BPM (= 75 BPM)
 * looped continuously. FearFeedbackLayer drives Howl.rate() per rAF so the
 * effective BPM scales with the local player's fear value (D-13 curve).
 * Loop:true + pool:1 enforces one heartbeat voice at a time; preload:false
 * follows the project-wide "load only after iOS unlock" pattern (P6-Pitfall 8).
 *
 * P6-Pitfall F mid-range mitigation: rate = bpm / P6_HEARTBEAT_BASE_BPM gives
 * moderate (0.4..1.33) pitch shift symmetric around the base, avoiding both
 * chipmunk highs and mud lows from a single sample.
 */
let heartbeatHowl: Howl | null = null;
let heartbeatPlaybackId: number | null = null;

// ===========================================================================
// Public singleton
// ===========================================================================

export const audioManager = {
  async init(): Promise<void> {
    if (initialized) return;
    if (!hasContentBundle()) return; // gate behind P5 hydration

    Howler.autoUnlock = true;

    // UI-06 bus split: seed the global master + per-bus gains from the
    // `hunter:settings` localStorage blob BEFORE any Howl plays (Pitfall E —
    // the first ambient/heartbeat play() must already reflect the saved
    // sliders, not the design defaults). Fail-open try/catch (InstallHint
    // idiom): a throwing/absent/malformed blob falls back to the
    // 07-UI-SPEC §Surface 2 defaults (master 100%, music 70%, SFX 100%).
    // Each gain is clamped to [0,1] (T-07-01 tampering mitigation).
    let master = P7_MASTER_DEFAULT;
    let music = P7_BUS_MUSIC_DEFAULT;
    let sfx = P7_BUS_SFX_DEFAULT;
    try {
      const raw = localStorage.getItem('hunter:settings');
      if (raw !== null) {
        const parsed = JSON.parse(raw) as Partial<{
          master: number;
          music: number;
          sfx: number;
        }>;
        if (typeof parsed.master === 'number') master = parsed.master;
        if (typeof parsed.music === 'number') music = parsed.music;
        if (typeof parsed.sfx === 'number') sfx = parsed.sfx;
      }
    } catch {
      // localStorage absent (SSR/jsdom) or malformed JSON — keep defaults.
    }
    audioManager.setVolumes({
      master: clampGain(master),
      music: clampGain(music),
      sfx: clampGain(sfx),
    });

    codec = selectCodec();

    // WARNING-3: load the audio manifest at runtime via fetch (NOT a
    // build-time JSON import). A static JSON import would force Vite to
    // bundle the manifest into the JS chunk, breaking the precache strategy
    // AND failing under jsdom (no bundler-baked JSON). The manifest already
    // lives in public/assets/audio/ so the dev server + Workbox precache
    // both serve it from /assets/audio/.
    const manifestRes = await fetch('/assets/audio/audio-manifest.json');
    if (!manifestRes.ok) {
      throw new Error(
        `audio-manifest.json fetch failed: ${manifestRes.status}`,
      );
    }
    const manifest = (await manifestRes.json()) as AudioManifest;

    initAmbient(codec, manifest);
    initMusic(codec, manifest);
    initStingers(codec, manifest);
    initWaffle(codec, manifest);
    initFootsteps(codec, manifest);

    // Plan 06-07 — heartbeat Howl. One looped sample drives the entire fear
    // BPM curve via Howl.rate (FearFeedbackLayer per-rAF setHeartbeatBpm
    // calls). preload:false + explicit load() follows the project-wide
    // post-iOS-unlock pattern. trackHowl registers the Howl with the BLOCKER-3
    // liveSources Set; the heartbeat is a long-lived loop so it contributes 1
    // to the AUDIO-07 ≤8 ceiling between play() and unload().
    heartbeatHowl = new Howl({
      src: srcListFor('heartbeat/heartbeat', manifest),
      loop: true,
      pool: 1,
      preload: false,
      volume: 0.45,
    });
    heartbeatHowl.load();
    audioManager.trackHowl(heartbeatHowl, 'sfx', 0.45);
    heartbeatPlaybackId = heartbeatHowl.play();

    // ===== One-way renderBus subscriptions (AUDIO-01) =====
    // 11 sim-event subscribers (the load-bearing minimum the plan asserts).
    subscriptions.push(
      renderBus.on('character-spoke', (e) => {
        const line = getQuote(e.characterId, e.eventTag, e.lineIndex);
        if (line === null) return;
        _playWaffleLine(e.entityId, e.characterId, line);
      }),
      renderBus.on('round-started', (e) => {
        decayDialogueFatigue();
        onRoundStarted(e);
      }),
      renderBus.on('round-ended', (e) => {
        onRoundEnded(e);
      }),
      renderBus.on('entity-moved', (e) => {
        _onEntityMoved(e);
      }),
      renderBus.on('torch-flicked', (e) => {
        _playStinger(e.on ? 'torch-on' : 'torch-off');
      }),
      renderBus.on('catch', () => {
        _playStinger('catch');
      }),
      // [Rule 3 - Blocking] The plan's interfaces snippet listed both
      // `'free-all'` and `'hider-released'` as renderBus events, but only
      // `'hider-released'` exists on RenderEventMap (`'free-all'` is a
      // sim-side EventTag for dialogue, not a bus variant — confirmed by
      // 06-01-SUMMARY: "Stage 9 hider-released maps to 'free-all' quote
      // bucket"). Fix: subscribe once to `'hider-released'` (the actual
      // rescue event) and fire the `'free-all'` stinger (the warm "longer
      // chime" per CONTEXT D-23). The `'base-touch'` stinger is registered
      // for a future event hook (no current sim emit triggers it; deferred).
      renderBus.on('hider-released', () => {
        _playStinger('free-all');
      }),
      renderBus.on('motion-light-on', () => {
        _playStinger('motion-light-hum');
      }),
      renderBus.on('motion-light-off', () => {
        // 06-06: fade-out the motion-light-hum loop (D-23 + VIS-05 alignment).
        stopMotionHum();
      }),
      // [Rule 3 - Blocking] To keep total subscriptions at ≥ 11 after
      // dropping the non-existent `'free-all'` event, subscribe to
      // `'shout'` (P4 D-09) as the spotted-shout audible cue. No stinger
      // mapping exists yet; the placeholder handler keeps the count at 11
      // for the manager.test.ts contract and gives 06-06 a clean seam to
      // wire a shout stinger when one is authored.
      renderBus.on('shout', () => {
        // 06-06 may wire a shout-flag stinger here; in 06-03 this is a
        // structural subscription only (count-preserving + future-proof).
      }),
      renderBus.on('fear-threshold-crossed', (e) => {
        // D-14: only the LOCAL player ('e0') drives fear feedback; only
        // upward crossings fire stingers; nervous threshold is too small a
        // jolt to interrupt, so afraid + terrified are the only stingers.
        if (e.entityId !== 'e0') return;
        if (e.direction !== 'up') return;
        if (e.threshold === 'afraid') _playStinger('fear-gasp-afraid');
        if (e.threshold === 'terrified') _playStinger('fear-gasp-terrified');
      }),
    );

    // Plan 06-07 — runtime-gated `audio-debug` emit. Forwards the real
    // liveSources count from Plan 03's BLOCKER-3 Set<number> registry to
    // DevOverlay (the single subscriber). The code ships in production builds
    // but the subscription is only registered when isDebugEnabled() is true
    // (?debug=1 or localStorage['hunter:debug']='1'). In dev / Vitest the
    // isDebugEnabled() fast-path always returns true.
    //
    // WARNING-5 closure: liveSources is the SOURCE OF TRUTH (audioManager
    // .getLiveSources() returns liveSources.size — the exact count of active
    // Howler playback IDs maintained by trackHowl listeners). NOT an
    // approximation. The debug emit is one line of forwarding.
    if (isDebugEnabled()) {
      subscriptions.push(
        renderBus.on('sim-tick', (e) => {
          renderBus.emit('audio-debug', {
            liveSources: audioManager.getLiveSources(),
            tick: e.tick,
          });
        }),
      );
    }

    initialized = true;
  },

  unlock: unlockHelper,

  playStinger(name: StingerName): void {
    _playStinger(name);
  },

  cancelWaffleLine(entityId: string): void {
    _cancelWaffle(entityId);
  },

  setHeartbeatBpm(bpm: number): void {
    // Plan 06-07 — D-13 BPM curve drives Howl.rate on the looped heartbeat
    // sample. P6-Pitfall F: rate = bpm / P6_HEARTBEAT_BASE_BPM gives a
    // moderate pitch shift symmetric around the recorded base BPM.
    // Idempotent guard: no-op if init has not run yet OR if the heartbeat
    // play hasn't seeded a playback id (e.g. during destroy/init transition).
    if (!heartbeatHowl || heartbeatPlaybackId === null) return;
    const rate = bpm / P6_HEARTBEAT_BASE_BPM;
    heartbeatHowl.rate(rate, heartbeatPlaybackId);
  },

  /**
   * BLOCKER-3 — submodules MUST call this on every Howl they construct in
   * 06-05/06-06. The manager wires 'play' to add the returned playback id
   * to liveSources, and 'end' / 'stop' to remove it. Together these prove
   * the cleanup path works (asserted by manager.test.ts Test 9 + Test 10).
   *
   * Total downstream Howls registered across the phase:
   *   - Plan 05: 2 (waffle atlas + footsteps atlas)
   *   - Plan 06: 11 (1 ambient + 2 music + 8 stingers)
   *   - Plan 07: 1 (heartbeat)
   *   = 14 distinct Howls. Cycle-safe at runtime because audioManager is
   *   only dereferenced inside each submodule's init function body, after
   *   the singleton is fully constructed.
   */
  trackHowl(howl: Howl, bus: AudioBus, designVolume: number): void {
    // UI-06 — record the Howl on its bus with its design volume so setVolumes
    // can recompute its group volume on every slider change.
    registry.push({ howl, bus, designVolume });
    howl.on('play', (id: number) => {
      liveSources.add(id);
    });
    howl.on('end', (id: number) => {
      liveSources.delete(id);
    });
    howl.on('stop', (id: number) => {
      liveSources.delete(id);
    });
  },

  /**
   * UI-06 bus split — the Settings panel's Master / Music / SFX sliders call
   * this. `master` drives the Howler global (multiplies every Howl); `music` /
   * `sfx` are per-bus gains. Each registered Howl's group volume is recomputed
   * to `designVolume * busGain[bus]`, so the music score and the SFX world
   * respond independently. Howler has no native bus — this registry IS the bus.
   */
  setVolumes({ master, music, sfx }: { master: number; music: number; sfx: number }): void {
    Howler.volume(master);
    busGain.music = music;
    busGain.sfx = sfx;
    for (const { howl, bus, designVolume } of registry) {
      howl.volume(designVolume * busGain[bus]);
    }
  },

  /**
   * UI-06 — read-only access to the live bus gain. Submodule fade targets
   * (music.ts / ambient.ts) read this at fade-start so a faded-in loop lands at
   * `designVolume * busGain[bus]` rather than the raw design volume. Pitfall A:
   * a slider move mid-fade lands on the NEXT fade event (a known minor).
   */
  getBusGain(bus: AudioBus): number {
    return busGain[bus];
  },

  /**
   * Real count of active Howler playback IDs — NOT an approximation. Used
   * by 06-07 DEV emit + 06-08 integration test for the AUDIO-07 ≤8
   * concurrent-source ceiling proof.
   */
  getLiveSources(): number {
    return liveSources.size;
  },

  destroy(): void {
    for (const off of subscriptions) off();
    subscriptions.length = 0;
    destroyAmbient();
    destroyMusic();
    destroyStingers();
    destroyWaffle();
    destroyFootsteps();
    // Plan 06-07 — drop the heartbeat Howl + clear the cached playback id so
    // setHeartbeatBpm safely no-ops between destroy() and the next init().
    heartbeatHowl?.unload();
    heartbeatHowl = null;
    heartbeatPlaybackId = null;
    liveSources.clear();
    // UI-06 — drop the bus registry so a fresh init() rebuilds it from zero.
    registry.length = 0;
    Howler.unload();
    initialized = false;
  },

  isInitialized(): boolean {
    return initialized;
  },
};
